import { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import type { AdminApiCredential } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';

interface CredentialRevokeDialogProps {
  open: boolean;
  credentialId: string;
  onClose: () => void;
  onRevoked: (credential: AdminApiCredential) => void;
}

/** `DELETE /v1/admin/api-credentials/{id}` — sets `status` to `revoked`; the row and its history are kept. */
export function CredentialRevokeDialog({ open, credentialId, onClose, onRevoked }: CredentialRevokeDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [reason, setReason] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function resetAndClose() {
    setReason('');
    setAccessDenied(false);
    onClose();
  }

  async function handleConfirm() {
    setSubmitting(true);
    try {
      const credential = await payApiClient.revokeAdminApiCredential(credentialId, { reason: reason.trim() || undefined });
      notify(translate('pay.credentials.revokeDialog.success'), { type: 'success' });
      setReason('');
      onRevoked(credential);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.credentials.revokeDialog.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Alert severity="error">
              <Typography variant="body2">{translate('pay.credentials.revokeDialog.body')}</Typography>
            </Alert>
            <TextField
              size="small"
              label={translate('pay.credentials.revokeDialog.fieldReason')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>{translate('pay.credentials.revokeDialog.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" color="error" onClick={handleConfirm} disabled={submitting}>
            {translate('pay.credentials.revokeDialog.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
